import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Reveal } from "@/components/Reveal";

function Counter({ to, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <span ref={ref} data-testid={`counter-${suffix || to}`}>
      {val}
      {suffix}
    </span>
  );
}

const STATS = [
  { value: 6, suffix: "", label: "Fundamental capabilities" },
  { value: 55, suffix: " min", label: "Every session, engineered" },
  { value: 7, suffix: "", label: "Qualities trained as one" },
  { value: 100, suffix: "%", label: "Coach-led, always" },
];

export default function Stats() {
  return (
    <section data-testid="small-group-section" className="relative bg-volt text-black py-24 sm:py-32 px-5 sm:px-10 lg:px-16 overflow-hidden">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Reveal>
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-extrabold mb-4">
              Small group. Personal attention.
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <p data-testid="athletes-per-session" className="font-display leading-[0.8] text-[42vw] sm:text-[24vw] lg:text-[16vw]">
              6–8
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-display uppercase text-3xl sm:text-5xl tracking-wide">Athletes per session</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs sm:text-sm uppercase tracking-[0.16em] font-bold">
              {["Small Groups", "Coach-Led Training", "Movement Quality", "Individual Progressions", "Community Energy"].map((t) => (
                <span key={t} className="border-b-2 border-black/70 pb-1">{t}</span>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 gap-px bg-black/20 border border-black/20">
          {STATS.map((s) => (
            <div key={s.label} className="bg-volt p-6 sm:p-10">
              <p className="font-display text-6xl sm:text-7xl leading-none">
                <Counter to={s.value} suffix={s.suffix} />
              </p>
              <p className="mt-3 text-xs sm:text-sm uppercase tracking-[0.14em] font-bold text-black/70">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
